import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  ChevronDown,
  Loader2,
  Share2,
  Check,
} from 'lucide-react';
import { format } from 'date-fns';
import { useBookingFormV2 } from './useBookingFormV2';
import { useFacility } from '../../context/FacilityContext';
import { encryptBookingParams } from '../../utils/linkCrypto';
import { getPublicWebBaseUrl } from '../../utils/publicWebBaseUrl';
import Notification from '../Notification';
import { isIndividualSportsService } from '../../utils/serviceType';
import { slotDisplayName as getSlotName, composeSlotLabel, cleanSlotRange } from '../../utils/slotName';
import { Card, InfoBanner } from '../ui';
import './BookingFormV2.css';

// GA-005: friendly labels for the pre-submit error summary.
const FIELD_LABELS = {
  timeSlot: 'Time slot',
  teamA_name: 'Team A name',
  teamA_captain: 'Team A captain',
  teamA_mobile: 'Team A mobile number',
  teamB_name: 'Team B name',
  teamB_captain: 'Team B captain',
  teamB_mobile: 'Team B mobile number',
};

/* ── Tag-based player input ─────────────────────────────────────── */
function PlayerTagInput({ value, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const tags = (value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const addTag = (raw) => {
    const name = raw.trim();
    if (!name || tags.includes(name)) return;
    onChange([...tags, name].join(', '));
    setInputValue('');
  };

  const removeTag = (idx) => {
    onChange(tags.filter((_, i) => i !== idx).join(', '));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div className="bfv2-tags-wrap" onClick={() => inputRef.current?.focus()}>
      {tags.map((tag, i) => (
        <span key={i} className="bfv2-tag">
          {tag}
          <button
            type="button"
            className="bfv2-tag-remove"
            onClick={(e) => {
              e.stopPropagation();
              removeTag(i);
            }}
          >
            &times;
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="bfv2-tag-input"
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (inputValue.trim()) addTag(inputValue);
        }}
        placeholder={tags.length === 0 ? 'Type name & press Enter' : ''}
      />
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────── */
const BookingFormV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    ground,
    service,
    groundId,
    serviceId,
    isMatch,
    isEditMode,
    configuredSlots,
    preselectedData,
    bookingData,
    bookingType,
    setBookingType,
    selectedTeamSide,
    setSelectedTeamSide,
    teamDetails,
    matchDetails,
    setMatchDetails,
    paymentDetails,
    setPaymentDetails,
    errors,
    isSubmitting,
    isLoading,
    showSuccess,
    confirmedBooking,
    getSelectedSlotPrice,
    isSlotHalfBooked,
    isSlotFullyBooked,
    alreadyBookedSide,
    availableTeamSide,
    getSlotConfiguredPrice,
    handleInputChange,
    handleTeamChange,
    handleSubmit,
    handleNotificationClose,
    handleNotificationConfirm,
    computeAmounts,
    contextLoading,
  } = useBookingFormV2();

  const { activeFacilityId } = useFacility();
  const [matchConfigOpen, setMatchConfigOpen] = useState(false);
  const [specialReqOpen, setSpecialReqOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const getPublicBookingLink = () => {
    const base = `${getPublicWebBaseUrl()}/book/${activeFacilityId}/${serviceId}`;
    const data = {};
    if (bookingData.date) data.date = bookingData.date;
    if (bookingData.slotId) data.slotId = bookingData.slotId;
    if (bookingType) data.bookingType = bookingType;
    const amounts = computeAmounts();
    if (amounts.totalAmount) data.price = amounts.totalAmount;
    if (amounts.advanceAmount) data.advance = amounts.advanceAmount;
    if (Object.keys(data).length === 0) return base;
    const token = encryptBookingParams(data);
    return `${base}?d=${token}`;
  };

  const handleShare = async () => {
    const url = getPublicBookingLink();
    const text = `Book your session: ${service?.name || 'Cricket Match'}${ground?.name ? ` at ${ground.name}` : ''}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: text, url });
        return;
      } catch (_) { /* user cancelled or not supported */ }
    }

    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (_) {
      window.prompt('Copy this link:', url);
    }
  };

  /* Context still loading grounds/services */
  if (contextLoading) {
    return (
      <div className="bfv2-container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <Loader2 size={40} className="bfv2-spin" style={{ margin: '0 auto 1rem', color: 'var(--ga-primary)' }} />
        <p className="bfv2-subtitle">Loading booking data&hellip;</p>
      </div>
    );
  }

  /* Service not found */
  if (!service) {
    return (
      <div className="bfv2-container" style={{ textAlign: 'center', paddingTop: '3rem' }}>
        <AlertCircle
          size={48}
          style={{ color: 'var(--ga-danger)', margin: '0 auto 1rem' }}
        />
        <h2 className="bfv2-title" style={{ marginBottom: '0.5rem' }}>
          Service Not Found
        </h2>
        <p className="bfv2-subtitle" style={{ marginBottom: '1.5rem' }}>
          The requested service doesn&apos;t exist.
        </p>
        <button
          className="bfv2-btn-confirm"
          style={{ maxWidth: 220, margin: '0 auto' }}
          onClick={() => navigate('/dashboard/book-now')}
        >
          Browse Services
        </button>
      </div>
    );
  }

  /* Redirect only individual sports; practice_net / open_net stay on team booking */
  if (isIndividualSportsService(service)) {
    navigate(`/dashboard/booking-new/${groundId}/${serviceId}`, {
      state: location.state,
    });
    return null;
  }

  /* Amounts for payment summary */
  const { baseAmount, discountAmount, totalAmount, advanceAmount, balanceAmount } =
    computeAmounts();

  /* Resolve displayed slot info */
  const allSlots = configuredSlots || service?.dailySlots || [];
  const selectedSlot =
    allSlots.find(
      (s) =>
        String(s.id) === String(bookingData.slotId) ||
        (s.startTime === preselectedData?.preselectedSlot?.startTime &&
          s.endTime === preselectedData?.preselectedSlot?.endTime)
    ) ||
    (preselectedData?.preselectedSlot && {
      id: preselectedData.preselectedSlot.id,
      startTime: preselectedData.preselectedSlot.startTime,
      endTime: preselectedData.preselectedSlot.endTime,
      name: preselectedData.preselectedSlot.name,
    });

  const slotTimeString = selectedSlot
    ? `${selectedSlot.startTime}-${selectedSlot.endTime}`
    : '';
  const slotDisplayName = selectedSlot
    ? getSlotName(selectedSlot.startTime, selectedSlot.endTime)
    : bookingData.timeSlot || 'No slot selected';

  /* GA-005: list of currently-invalid fields for the pre-submit summary. */
  const errorFieldKeys = Object.keys(errors).filter(
    (k) => errors[k] && k !== 'submit'
  );

  return (
    <div className="bfv2-container">
      {/* Loading overlay */}
      {isLoading && (
        <div className="bfv2-loading-overlay">
          <div className="bfv2-loading-box">
            <div className="bfv2-loading-spinner" />
            <span className="bfv2-loading-text">Processing booking…</span>
          </div>
        </div>
      )}

      {/* ─── Header ─────────────────────────────────────────── */}
      <div className="bfv2-header">
        <div className="bfv2-header-top-row">
          <button
            type="button"
            className="bfv2-back-link"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} /> Back
          </button>

          <button
            type="button"
            className={`bfv2-share-btn ${shareCopied ? 'bfv2-share-btn--copied' : ''}`}
            onClick={handleShare}
            title="Share public booking link"
          >
            {shareCopied ? <Check size={16} /> : <Share2 size={16} />}
            {shareCopied ? 'Link Copied!' : 'Share'}
          </button>
        </div>

        <div className="bfv2-flex-row">
          <h1 className="bfv2-title">
            {isEditMode ? 'Edit Booking' : 'Book Your Session'}
          </h1>
          {isEditMode && (
            <span className="bfv2-badge bfv2-badge--edit">Edit Mode</span>
          )}
          {isSlotFullyBooked && (
            <span className="bfv2-badge bfv2-badge--full">Fully Booked</span>
          )}
        </div>

        <p className="bfv2-subtitle">
          {service?.name || 'Cricket Match'}
          {ground?.name ? ` — ${ground.name}` : ''}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ─── Slot summary (sticky context at top) ───────────── */}
        {selectedSlot && (
          <Card
            padding="sm"
            className="mb-4 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="shrink-0 text-ga-primary" />
              <div className="min-w-0">
                <div className="truncate font-semibold text-ga-text-primary">
                  {composeSlotLabel(slotDisplayName, slotTimeString)}
                </div>
                <div className="text-xs text-ga-text-muted">
                  {bookingData.date
                    ? format(new Date(bookingData.date), 'EEE, MMM dd, yyyy')
                    : 'Date not selected'}
                  {slotTimeString ? ` · ${cleanSlotRange(slotTimeString)}` : ''}
                </div>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-xs text-ga-text-muted">Price</div>
              <div className="text-lg font-bold text-ga-primary">
                ₹{getSelectedSlotPrice || 0}
              </div>
            </div>
          </Card>
        )}

        {/* ─── Section 1 — Date & Slot ────────────────────────── */}
        <div className="bfv2-card">
          <div className="bfv2-section-title" style={{ marginBottom: '0.75rem' }}>
            <Calendar size={18} style={{ color: 'var(--ga-primary)' }} />
            Date &amp; Time Slot
          </div>

          <div className="bfv2-field-group">
            <label className="bfv2-label">Booking Date</label>
            {preselectedData?.preselectedDate ? (
              <div className="bfv2-date-display">
                <Calendar size={14} />
                {format(new Date(bookingData.date), 'EEEE, MMMM dd, yyyy')}
              </div>
            ) : (
              <input
                type="date"
                className="bfv2-input"
                value={bookingData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                min={format(new Date(Date.now() + 86400000), 'yyyy-MM-dd')}
              />
            )}
          </div>

          {preselectedData?.preselectedSlot || bookingData.slotId ? (
            <div className="bfv2-slot-display" data-field="timeSlot">
              <div>
                <div className="bfv2-flex-row">
                  <CheckCircle size={18} style={{ color: 'var(--ga-primary)' }} />
                  <span className="bfv2-slot-name">{slotDisplayName}</span>
                </div>
                {preselectedData?.preselectedSlot && (
                  <span className="bfv2-slot-source">Selected from Calendar</span>
                )}
              </div>
              <div className="bfv2-slot-price">
                <div className="bfv2-slot-price-label">Price</div>
                <div className="bfv2-slot-price-value">
                  ₹{getSelectedSlotPrice || 0}
                </div>
              </div>
            </div>
          ) : (
            <div className="bfv2-field-group">
              <label className="bfv2-label">Select Time Slot</label>
              <div className="bfv2-slot-grid" data-field="timeSlot">
                {allSlots.map((slot) => {
                  const st = `${slot.startTime}-${slot.endTime}`;
                  const isSelected =
                    String(bookingData.slotId) === String(slot.id);
                  const cfgPrice = getSlotConfiguredPrice(
                    slot.id,
                    bookingData.date
                  );
                  const displayPrice =
                    cfgPrice ?? slot.defaultPrice ?? slot.price ?? 0;
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      className={`bfv2-slot-option ${isSelected ? 'bfv2-slot-option--selected' : ''}`}
                      onClick={() => {
                        handleInputChange('timeSlot', st);
                        handleInputChange('slotId', slot.id);
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{getSlotName(slot.startTime, slot.endTime)}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--ga-text-muted)', marginTop: '0.125rem' }}>
                        {st}
                      </div>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--ga-text-muted)',
                          marginTop: '0.25rem',
                        }}
                      >
                        ₹{displayPrice}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {errors.timeSlot && <p className="bfv2-error">{errors.timeSlot}</p>}
        </div>

        {/* ─── Section 2 — Team Booking Type ──────────────────── */}
        <div className="bfv2-card">
          <div className="bfv2-section-title" style={{ marginBottom: '0.75rem' }}>
            <Users size={18} style={{ color: 'var(--ga-primary)' }} />
            Team Booking Type
          </div>

          {isSlotHalfBooked && (
            <div className="bfv2-alert bfv2-alert--warn" style={{ marginBottom: '0.75rem' }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong>Slot is Half-Booked</strong>
                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {alreadyBookedSide === 'teamA' ? 'Team A' : 'Team B'} has already booked.
                  Only {availableTeamSide === 'teamA' ? 'Team A' : 'Team B'} (Single Team Booking) is available.
                </div>
              </div>
            </div>
          )}

          <div className="bfv2-toggle-group">
            <button
              type="button"
              className={`bfv2-toggle ${bookingType === 'both' ? 'bfv2-toggle--active' : ''}`}
              disabled={isSlotHalfBooked}
              onClick={() => setBookingType('both')}
            >
              Book Both Teams
              {isSlotHalfBooked && (
                <div style={{ fontSize: '0.625rem', marginTop: '0.25rem', opacity: 0.7 }}>
                  (Unavailable)
                </div>
              )}
            </button>
            <button
              type="button"
              className={`bfv2-toggle ${bookingType === 'single' ? 'bfv2-toggle--active' : ''}`}
              onClick={() => setBookingType('single')}
            >
              Single Team
            </button>
          </div>

          {bookingType === 'single' && (
            <div style={{ marginTop: '0.75rem' }}>
              <span className="bfv2-label">Select Team Side</span>
              <div className="bfv2-pills">
                <button
                  type="button"
                  className={`bfv2-pill ${selectedTeamSide === 'teamA' ? 'bfv2-pill--active' : ''}`}
                  disabled={isSlotHalfBooked && alreadyBookedSide === 'teamA'}
                  onClick={() => setSelectedTeamSide('teamA')}
                >
                  Team A{isSlotHalfBooked && alreadyBookedSide === 'teamA' ? ' (Booked)' : ''}
                </button>
                <button
                  type="button"
                  className={`bfv2-pill ${selectedTeamSide === 'teamB' ? 'bfv2-pill--active' : ''}`}
                  disabled={isSlotHalfBooked && alreadyBookedSide === 'teamB'}
                  onClick={() => setSelectedTeamSide('teamB')}
                >
                  Team B{isSlotHalfBooked && alreadyBookedSide === 'teamB' ? ' (Booked)' : ''}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Section 3 — Team Details ───────────────────────── */}
        <div className="bfv2-card">
          <div className="bfv2-section-title" style={{ marginBottom: '0.5rem' }}>
            <Users size={18} style={{ color: 'var(--ga-primary)' }} />
            Team Details
          </div>

          {['teamA', 'teamB'].map((tk) => {
            if (bookingType === 'single' && tk !== selectedTeamSide) return null;
            const label = tk === 'teamA' ? 'Team A' : 'Team B';
            return (
              <div key={tk} className="bfv2-team-card">
                <div className="bfv2-team-label">
                  {label}
                  {bookingType === 'single' && (
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--ga-primary)',
                        fontWeight: 400,
                      }}
                    >
                      (Selected)
                    </span>
                  )}
                </div>

                <div className="bfv2-grid-2">
                  <div className="bfv2-field-group">
                    <label className="bfv2-label">Team Name *</label>
                    <input
                      className="bfv2-input"
                      type="text"
                      data-field={`${tk}_name`}
                      placeholder="Enter team name"
                      value={teamDetails[tk].name}
                      onChange={(e) =>
                        handleTeamChange(tk, 'name', e.target.value)
                      }
                    />
                    {errors[`${tk}_name`] && (
                      <p className="bfv2-error">{errors[`${tk}_name`]}</p>
                    )}
                  </div>
                  <div className="bfv2-field-group">
                    <label className="bfv2-label">Captain Name *</label>
                    <input
                      className="bfv2-input"
                      type="text"
                      data-field={`${tk}_captain`}
                      placeholder="Enter captain name"
                      value={teamDetails[tk].captain}
                      onChange={(e) =>
                        handleTeamChange(tk, 'captain', e.target.value)
                      }
                    />
                    {errors[`${tk}_captain`] && (
                      <p className="bfv2-error">{errors[`${tk}_captain`]}</p>
                    )}
                  </div>
                </div>

                <div className="bfv2-field-group">
                  <label className="bfv2-label">Mobile Number *</label>
                  <input
                    className="bfv2-input"
                    type="tel"
                    data-field={`${tk}_mobile`}
                    placeholder="10-digit mobile number"
                    value={teamDetails[tk].mobile}
                    onChange={(e) =>
                      handleTeamChange(tk, 'mobile', e.target.value)
                    }
                  />
                  {errors[`${tk}_mobile`] && (
                    <p className="bfv2-error">{errors[`${tk}_mobile`]}</p>
                  )}
                </div>

                <div className="bfv2-field-group">
                  <label className="bfv2-label">Players (optional — type name &amp; press Enter)</label>
                  <PlayerTagInput
                    value={teamDetails[tk].players}
                    onChange={(val) => handleTeamChange(tk, 'players', val)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* ─── Section 4 — Match Configuration (collapsible) ──── */}
        <div className="bfv2-card">
          <div
            className="bfv2-section-header"
            onClick={() => setMatchConfigOpen((p) => !p)}
          >
            <div className="bfv2-section-title">
              <Clock size={18} style={{ color: 'var(--ga-primary)' }} />
              Match Configuration
            </div>
            <ChevronDown
              size={18}
              className={`bfv2-section-chevron ${matchConfigOpen ? 'bfv2-section-chevron--open' : ''}`}
            />
          </div>

          {matchConfigOpen && (
            <div className="bfv2-section-body">
              <div className="bfv2-field-group">
                <label className="bfv2-label">Ball Type</label>
                <div className="bfv2-chips">
                  {['leather', 'tennis'].map((bt) => (
                    <button
                      key={bt}
                      type="button"
                      className={`bfv2-chip ${matchDetails.ballType === bt ? 'bfv2-chip--active' : ''}`}
                      onClick={() =>
                        setMatchDetails((p) => ({ ...p, ballType: bt }))
                      }
                    >
                      {bt.charAt(0).toUpperCase() + bt.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bfv2-field-group">
                <label className="bfv2-label">Ball Color</label>
                <div className="bfv2-chips">
                  {['red', 'white'].map((bc) => (
                    <button
                      key={bc}
                      type="button"
                      className={`bfv2-chip ${matchDetails.ballColor === bc ? 'bfv2-chip--active' : ''}`}
                      onClick={() =>
                        setMatchDetails((p) => ({ ...p, ballColor: bc }))
                      }
                    >
                      {bc.charAt(0).toUpperCase() + bc.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bfv2-grid-2">
                <div className="bfv2-field-group">
                  <label className="bfv2-label">Umpires</label>
                  <div className="bfv2-chips">
                    {['1', '2'].map((u) => (
                      <button
                        key={u}
                        type="button"
                        className={`bfv2-chip ${matchDetails.umpire === u ? 'bfv2-chip--active' : ''}`}
                        onClick={() =>
                          setMatchDetails((p) => ({ ...p, umpire: u }))
                        }
                      >
                        {u} Umpire{u === '2' ? 's' : ''}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bfv2-field-group">
                  <label className="bfv2-label">Overs</label>
                  <input
                    className="bfv2-input"
                    type="number"
                    min="5"
                    max="50"
                    value={matchDetails.oversCount}
                    onChange={(e) =>
                      setMatchDetails((p) => ({
                        ...p,
                        oversCount: parseInt(e.target.value) || 20,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── Section 5 — Special Requests (collapsible) ─────── */}
        <div className="bfv2-card">
          <div
            className="bfv2-section-header"
            onClick={() => setSpecialReqOpen((p) => !p)}
          >
            <div className="bfv2-section-title">Special Requests</div>
            <ChevronDown
              size={18}
              className={`bfv2-section-chevron ${specialReqOpen ? 'bfv2-section-chevron--open' : ''}`}
            />
          </div>

          {specialReqOpen && (
            <div className="bfv2-section-body">
              <textarea
                className="bfv2-input"
                rows="3"
                placeholder="Any special requirements or requests…"
                value={bookingData.specialRequests}
                onChange={(e) =>
                  handleInputChange('specialRequests', e.target.value)
                }
              />
            </div>
          )}
        </div>

        {/* ─── Section 6 — Payment ────────────────────────────── */}
        <div className="bfv2-card">
          <div className="bfv2-section-title" style={{ marginBottom: '0.75rem' }}>
            Payment Information
          </div>

          {/* Payment mode */}
          <div className="bfv2-field-group">
            <label className="bfv2-label">Payment Mode</label>
            <div className="bfv2-toggle-group">
              {['cash', 'online'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`bfv2-toggle ${paymentDetails.paymentMode === mode ? 'bfv2-toggle--active' : ''}`}
                  onClick={() =>
                    setPaymentDetails((p) => ({ ...p, paymentMode: mode }))
                  }
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Discount */}
          <div className="bfv2-field-group">
            <label className="bfv2-label">Discount</label>
            <div className="bfv2-grid-2">
              <select
                className="bfv2-input"
                value={paymentDetails.discountType}
                onChange={(e) =>
                  setPaymentDetails((p) => ({
                    ...p,
                    discountType: e.target.value,
                    discountAmount: 0,
                  }))
                }
              >
                <option value="amount">Amount (₹)</option>
                <option value="percentage">Percentage (%)</option>
              </select>
              <input
                className="bfv2-input"
                type="number"
                min="0"
                max={
                  paymentDetails.discountType === 'percentage'
                    ? 100
                    : baseAmount
                }
                step="any"
                value={paymentDetails.discountAmount || ''}
                onChange={(e) => {
                  const raw =
                    e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                  const cap =
                    paymentDetails.discountType === 'percentage'
                      ? 100
                      : baseAmount;
                  setPaymentDetails((p) => ({
                    ...p,
                    discountAmount: Math.max(0, Math.min(raw, cap)),
                  }));
                }}
                placeholder={
                  paymentDetails.discountType === 'percentage'
                    ? 'Enter %'
                    : 'Enter amount'
                }
              />
            </div>
          </div>

          {/* Advance amount */}
          <div className="bfv2-field-group">
            <label className="bfv2-label">Advance Amount (₹)</label>
            <input
              className="bfv2-input"
              type="number"
              min="0"
              max={totalAmount}
              step="any"
              value={paymentDetails.advanceAmount || ''}
              onChange={(e) => {
                const raw =
                  e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                setPaymentDetails((p) => ({
                  ...p,
                  advanceAmount: Math.max(0, Math.min(raw, totalAmount)),
                }));
              }}
              placeholder="Enter advance amount"
            />
          </div>

          {/* Summary */}
          <div className="bfv2-summary">
            {bookingType === 'single' && (
              <div className="bfv2-summary-row" style={{ fontSize: '0.75rem' }}>
                <span>Slot Price (Full)</span>
                <span>₹{(baseAmount * 2).toFixed(2)}</span>
              </div>
            )}

            <div className="bfv2-summary-row">
              <span>
                Base Amount
                {bookingType === 'single' ? ' (Single Team)' : ''}
              </span>
              <span>₹{baseAmount.toFixed(2)}</span>
            </div>

            {discountAmount > 0 && (
              <div className="bfv2-summary-row bfv2-summary-row--discount">
                <span>
                  Discount (
                  {paymentDetails.discountType === 'percentage'
                    ? `${paymentDetails.discountAmount}%`
                    : 'Amount'}
                  )
                </span>
                <span>- ₹{discountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="bfv2-summary-row bfv2-summary-row--total">
              <span>Total Amount</span>
              <span className="bfv2-summary-value">₹{totalAmount.toFixed(2)}</span>
            </div>

            <div className="bfv2-summary-row">
              <span>Advance Amount</span>
              <span>₹{advanceAmount.toFixed(2)}</span>
            </div>

            {totalAmount > 0 && (
              <div className="bfv2-summary-row" style={{ fontSize: '0.75rem' }}>
                <span>Payment Mode</span>
                <span style={{ textTransform: 'capitalize' }}>
                  {paymentDetails.paymentMode}
                </span>
              </div>
            )}

            {balanceAmount > 0 && (
              <div className="bfv2-summary-row bfv2-summary-row--balance">
                <span>Balance (Pending)</span>
                <span>₹{balanceAmount.toFixed(2)}</span>
              </div>
            )}

            {balanceAmount === 0 && advanceAmount > 0 && (
              <div className="bfv2-summary-row bfv2-summary-row--paid">
                <span>Status</span>
                <span>Fully Paid</span>
              </div>
            )}
          </div>
        </div>

        {/* ─── Submit ─────────────────────────────────────────── */}
        {errors.submit && (
          <div className="bfv2-alert bfv2-alert--error" style={{ marginBottom: '1rem' }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{errors.submit}</span>
          </div>
        )}

        {/* GA-005: compact error summary right above the Confirm button so a
            failed submit is never silent, even when the invalid field is far
            above the fold. */}
        {errorFieldKeys.length > 0 && (
          <InfoBanner
            variant="danger"
            title="Please fix the following before confirming"
            className="mb-3"
          >
            <ul className="list-disc space-y-0.5 pl-4">
              {errorFieldKeys.map((k) => (
                <li key={k}>
                  {FIELD_LABELS[k] || k}: {errors[k]}
                </li>
              ))}
            </ul>
          </InfoBanner>
        )}

        <button
          type="submit"
          className="bfv2-btn-confirm"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="bfv2-loading-spinner" style={{ border: 'none', width: 18, height: 18 }} />
              {isEditMode ? 'Updating…' : 'Confirming…'}
            </>
          ) : isEditMode ? (
            'Update Booking'
          ) : (
            'Confirm Booking'
          )}
        </button>
      </form>

      {/* ─── Success modal (reuses V1 Notification) ───────────── */}
      {showSuccess && confirmedBooking && (
        <Notification
          booking={confirmedBooking}
          matchDetails={matchDetails}
          onClose={handleNotificationClose}
          onConfirm={handleNotificationConfirm}
        />
      )}
    </div>
  );
};

export default BookingFormV2;
